@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalFoundationApi::class)

package com.example.monetix.ui.screens

import androidx.compose.animation.core.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import com.example.monetix.ui.components.MonetixTextField as OutlinedTextField
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import app.cash.sqldelight.async.coroutines.awaitAsList
import com.example.monetix.db.MonetixDatabase
import com.example.monetix.db.TransactionEntity
import com.example.monetix.HomeViewModel
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlin.math.abs

enum class TrashAction { DELETE_SELECTED, DELETE_ALL, RESTORE_SELECTED, RESTORE_ALL, NONE }

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TrashScreen(
    db: MonetixDatabase,
    viewModel: HomeViewModel,
    onBack: () -> Unit
) {
    val scope = rememberCoroutineScope()
    var deletedTransactions by remember { mutableStateOf<List<TransactionEntity>>(emptyList()) }

    var selectedIds by remember { mutableStateOf(emptySet<String>()) }
    var pendingAction by remember { mutableStateOf(TrashAction.NONE) }

    val snackbarHostState = remember { SnackbarHostState() }

    val isSelectionMode = selectedIds.isNotEmpty()

    LaunchedEffect(Unit) {
        try {
            deletedTransactions = db.transactionQueries.getDeletedTransactions().awaitAsList()
        } catch (e: Exception) { }
    }

    val infiniteTransition = rememberInfiniteTransition()
    val bgOffset by infiniteTransition.animateFloat(
        initialValue = 0f,
        targetValue = 1000f,
        animationSpec = infiniteRepeatable(
            animation = tween(20000, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse
        ),
        label = "bg_offset"
    )

    // ==========================================
    // 🚀 POPUP UNIFICATO (STYLE VETRO)
    // ==========================================
    if (pendingAction != TrashAction.NONE) {
        val isDelete = pendingAction == TrashAction.DELETE_ALL || pendingAction == TrashAction.DELETE_SELECTED
        val isAll = pendingAction == TrashAction.DELETE_ALL || pendingAction == TrashAction.RESTORE_ALL

        val title = if (isDelete) "Elimina definitivamente" else "Ripristina transazioni"
        val text = when {
            isDelete && isAll -> "Vuoi svuotare completamente il cestino? L'azione è irreversibile."
            isDelete && !isAll -> "Vuoi eliminare definitivamente le ${selectedIds.size} transazioni selezionate?"
            !isDelete && isAll -> "Vuoi ripristinare tutte le transazioni del cestino?"
            else -> "Vuoi ripristinare le ${selectedIds.size} transazioni selezionate?"
        }

        AlertDialog(
            onDismissRequest = { pendingAction = TrashAction.NONE },
            shape = RoundedCornerShape(28.dp),
            containerColor = Color.White.copy(alpha = 0.12f),
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        if (isDelete) Icons.Default.DeleteForever else Icons.Default.Restore,
                        contentDescription = null,
                        tint = if (isDelete) Color(0xFFEF4444) else Color(0xFF10B981),
                        modifier = Modifier.size(24.dp)
                    )
                    Spacer(Modifier.width(8.dp))
                    Text(
                        title,
                        style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White
                    )
                }
            },
            text = { Text(text, style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0)) },
            confirmButton = {
                Button(
                    shape = RoundedCornerShape(14.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = if (isDelete) Color(0xFFEF4444) else Color(0xFF10B981),
                        contentColor = Color.White
                    ),
                    onClick = {
                        val currentAction = pendingAction
                        val currentSelectedIds = selectedIds.toSet()

                        scope.launch(Dispatchers.Default) {
                            val txsToProcess = when (currentAction) {
                                TrashAction.DELETE_SELECTED, TrashAction.RESTORE_SELECTED -> deletedTransactions.filter { it.id in currentSelectedIds }
                                TrashAction.DELETE_ALL, TrashAction.RESTORE_ALL -> deletedTransactions
                                TrashAction.NONE -> emptyList()
                            }

                            // 🚀 1. OPERAZIONE LOCALE VELOCISSIMA E SENZA INTERNET
                            if (isDelete) {
                                txsToProcess.forEach { tx ->
                                    try { db.transactionQueries.deleteById(tx.id) } catch (e: Exception) {}
                                }
                            }

                            val processedIds = txsToProcess.map { it.id }.toSet()
                            val successMessage = if (isDelete) "Eliminazione completata con successo!" else "Movimenti ripristinati!"

                            // 🚀 2. AGGIORNA LA UI SUBITO
                            withContext(Dispatchers.Main) {
                                deletedTransactions = deletedTransactions.filter { it.id !in processedIds }
                                selectedIds = emptySet()
                                pendingAction = TrashAction.NONE

                                scope.launch {
                                    snackbarHostState.showSnackbar(successMessage, duration = SnackbarDuration.Short)
                                }
                            }

                            // 🚀 3. MANDA AL CLOUD IN BACKGROUND
                            if (!isDelete) {
                                txsToProcess.forEach { tx ->
                                    val restoredTx = tx.copy(isDeleted = false, updatedAtEpochMs = Clock.System.now().toEpochMilliseconds())
                                    try { viewModel.saveTransaction(restoredTx) } catch (e: Exception) {}
                                }
                            } else {
                                txsToProcess.forEach { tx ->
                                    try { viewModel.firestore.elimina("transazioni", tx.id) } catch (e: Exception) {}
                                }
                            }
                        }
                    }
                ) {
                    Text(if (isDelete) "ELIMINA" else "RIPRISTINA", fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { pendingAction = TrashAction.NONE }) {
                    Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold)
                }
            }
        )
    }

    val hasCustomBg = com.example.monetix.LocalHasCustomBackground.current

    Box(
        modifier = Modifier
            .fillMaxSize()
            .then(
                if (hasCustomBg) Modifier.background(Color.Transparent)
                else Modifier.background(
                    Brush.verticalGradient(
                        colors = listOf(
                            Color(0xFF4F46E5).copy(alpha = 0.8f),
                            Color(0xFF1E1B4B),
                            Color(0xFF0F172A)
                        ),
                        startY = 0f,
                        endY = bgOffset + 1500f
                    )
                )
            )
    ) {
        Scaffold(
            snackbarHost = { SnackbarHost(hostState = snackbarHostState) },
            topBar = {
                if (isSelectionMode) {
                    TopAppBar(
                        title = { Text("${selectedIds.size} selezionati", fontWeight = FontWeight.Bold, color = Color.White) },
                        navigationIcon = {
                            IconButton(onClick = { selectedIds = emptySet() }) {
                                Icon(Icons.Default.Close, contentDescription = "Annulla selezione", tint = Color.White)
                            }
                        },
                        actions = {
                            IconButton(onClick = { pendingAction = TrashAction.RESTORE_SELECTED }) {
                                Icon(Icons.Default.Restore, contentDescription = "Ripristina", tint = Color(0xFF10B981))
                            }
                            IconButton(onClick = { pendingAction = TrashAction.DELETE_SELECTED }) {
                                Icon(Icons.Default.DeleteForever, contentDescription = "Elimina", tint = Color(0xFFEF4444))
                            }
                        },
                        colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.White.copy(alpha = 0.1f))
                    )
                } else {
                    TopAppBar(
                        title = { Text("Cestino", fontWeight = FontWeight.Bold, color = Color.White) },
                        navigationIcon = {
                            IconButton(onClick = onBack) { Icon(Icons.Default.ArrowBack, contentDescription = "Indietro", tint = Color.White) }
                        },
                        actions = {
                            if (deletedTransactions.isNotEmpty()) {
                                IconButton(onClick = { pendingAction = TrashAction.RESTORE_ALL }) {
                                    Icon(Icons.Default.Restore, contentDescription = "Ripristina Tutto", tint = Color(0xFF10B981))
                                }
                                IconButton(onClick = { pendingAction = TrashAction.DELETE_ALL }) {
                                    Icon(Icons.Default.DeleteSweep, contentDescription = "Svuota Cestino", tint = Color(0xFFEF4444))
                                }
                            }
                        },
                        colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent)
                    )
                }
            },
            containerColor = Color.Transparent
        ) { padding ->
            if (deletedTransactions.isEmpty()) {
                Box(modifier = Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                    Text("Il cestino è vuoto.", color = Color.White.copy(alpha = 0.6f), style = MaterialTheme.typography.bodyLarge)
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxSize().padding(padding),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    items(deletedTransactions, key = { it.id }) { tx ->
                        val isSelected = selectedIds.contains(tx.id)

                        TrashTransactionCard(
                            transaction = tx,
                            isSelected = isSelected,
                            isSelectionMode = isSelectionMode,
                            onToggleSelect = {
                                selectedIds = if (isSelected) selectedIds - tx.id else selectedIds + tx.id
                            },
                            onRestoreSingle = {
                                selectedIds = setOf(tx.id)
                                pendingAction = TrashAction.RESTORE_SELECTED
                            },
                            onPermanentDeleteSingle = {
                                selectedIds = setOf(tx.id)
                                pendingAction = TrashAction.DELETE_SELECTED
                            }
                        )
                    }
                }
            }
        }
    }
}

@OptIn(ExperimentalFoundationApi::class)
@Composable
fun TrashTransactionCard(
    transaction: TransactionEntity,
    isSelected: Boolean,
    isSelectionMode: Boolean,
    onToggleSelect: () -> Unit,
    onRestoreSingle: () -> Unit,
    onPermanentDeleteSingle: () -> Unit
) {
    val euros = abs(transaction.amountCents) / 100
    val cents = (abs(transaction.amountCents) % 100).toString().padStart(2, '0')
    val sign = if (transaction.type == "EXPENSE") "-" else "+"
    val amountColor = if (transaction.type == "EXPENSE") Color.White else Color(0xFF10B981)

    val dateStr = try {
        val d = LocalDate.fromEpochDays(transaction.dateEpochDay.toInt())
        "${d.dayOfMonth.toString().padStart(2, '0')}/${d.monthNumber.toString().padStart(2, '0')}/${d.year}"
    } catch (e: Exception) { "" }

    Surface(
        shape = RoundedCornerShape(20.dp),
        color = if (isSelected) Color.White.copy(alpha = 0.18f) else Color.White.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, if (isSelected) Color(0xFF60A5FA) else Color.White.copy(alpha = 0.15f)),
        modifier = Modifier
            .fillMaxWidth()
            .combinedClickable(
                onLongClick = { onToggleSelect() },
                onClick = {
                    if (isSelectionMode) {
                        onToggleSelect()
                    }
                }
            )
    ) {
        Row(
            modifier = Modifier.padding(16.dp).fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier.size(48.dp).clip(CircleShape).background(Color.White.copy(alpha = 0.1f)),
                contentAlignment = Alignment.Center
            ) {
                if (isSelected) {
                    Icon(Icons.Default.Check, contentDescription = "Selezionato", tint = Color(0xFF60A5FA))
                } else {
                    Text(transaction.logoEmoji ?: "📄", fontSize = 24.sp)
                }
            }
            Spacer(Modifier.width(12.dp))
            Column(modifier = Modifier.weight(1f)) {
                Text(transaction.description ?: "Transazione", fontWeight = FontWeight.Bold, color = Color.White, maxLines = 1)
                Spacer(Modifier.height(2.dp))
                Text("$dateStr • ${transaction.merchantKey ?: ""}", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
                Spacer(Modifier.height(2.dp))
                Text(text = "$sign$euros,$cents €", fontWeight = FontWeight.ExtraBold, color = amountColor)
            }

            if (!isSelectionMode) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    IconButton(
                        onClick = onRestoreSingle,
                        modifier = Modifier.size(40.dp).background(Color(0xFF10B981).copy(alpha = 0.15f), CircleShape)
                    ) {
                        Icon(Icons.Default.Restore, contentDescription = "Ripristina", tint = Color(0xFF10B981))
                    }

                    IconButton(
                        onClick = onPermanentDeleteSingle,
                        modifier = Modifier.size(40.dp).background(Color(0xFFEF4444).copy(alpha = 0.15f), CircleShape)
                    ) {
                        Icon(Icons.Default.DeleteForever, contentDescription = "Elimina", tint = Color(0xFFEF4444))
                    }
                }
            }
        }
    }
}